import { Box, Card, Flex, Grid, Text, Button, Icon, useToast, Tooltip } from '@chakra-ui/react'
import { PiggyBank, Heart, GraduationCap, Car, Plane, DollarSign, Umbrella, Package } from 'lucide-react'
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleFlag } from 'react-circle-flags'
import currencySymbolMap from 'currency-symbol-map'
import { getCountryNameByCode } from '../countries/PhoneDial'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { fetchBenefits, fetchEmployeeList, deleteBenefit } from '../../api/workforce/workforce'
import formatDate from '../../helpers/formatDate'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import AddBenefitModal from '../modals/addBenefitModal'
import handleToast from '../HandleToast'
import AvatarCell from './base/AvatarCell'

import {  ExportIcon } from '../../assets/icons'
import {
  exportBenefitsToCsv,
  exportBenefitsToXls,
} from '../../api/workforce/workforce'

const BENEFIT_TYPE_CONFIG: Record<string, { bg: string; color: string; icon: React.ElementType }> = {
  pension: {
    bg: 'success.50',
    color: 'success.200',
    icon: PiggyBank,
  },
  health: {
    bg: 'error.0',
    color: 'error.100',
    icon: Heart,
  },
  education: {
    bg: 'purple.50',
    color: 'purple.500',
    icon: GraduationCap,
  },
  vehicle: {
    bg: 'aqua.0',
    color: 'aqua.100',
    icon: Car,
  },
  travel_allowance: {
    bg: 'aqua.0',
    color: 'aqua.200',
    icon: Plane,
  },
  extra_month_salary: {
    bg: 'warning.0',
    color: 'warning.100',
    icon: DollarSign,
  },
  vacation_allowance: {
    bg: 'purple.0',
    color: 'purple.400',
    icon: Umbrella,
  },
  other: {
    bg: 'solid.50',
    color: 'solid.700',
    icon: Package,
  },
}


const BENEFIT_TYPE_FALLBACK: Record<string, string> = {
  pension: 'Pension',
  health: 'Health',
  education: 'Education',
  vehicle: 'Vehicle',
  travel_allowance: 'Travel allowance',
  extra_month_salary: 'Extra month salary',
  vacation_allowance: 'Vacation allowance',
  other: 'Other',
}

const BENEFIT_FREQUENCY_FALLBACK: Record<string, string> = {
  single: 'Single',
  monthly: 'Monthly',
  annual: 'Annual',
}

const BENEFIT_TYPE_OPTIONS = [
  { id: 'pension', name: 'Pension' },
  { id: 'health', name: 'Health' },
  { id: 'education', name: 'Education' },
  { id: 'vehicle', name: 'Vehicle' },
  { id: 'travel_allowance', name: 'Travel allowance' },
  { id: 'extra_month_salary', name: 'Extra month salary' },
  { id: 'vacation_allowance', name: 'Vacation allowance' },
  { id: 'other', name: 'Other' },
]

const BENEFIT_FREQUENCY_OPTIONS = [
  { id: 'single', name: 'Single' },
  { id: 'monthly', name: 'Monthly' },
  { id: 'annual', name: 'Annual' },
]

interface BenefitsTableProps {
  employeeId?: number
  isDisabled?: boolean
}

const BenefitsTable: React.FC<BenefitsTableProps> = ({ 
  employeeId, 
  isDisabled = false,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingBenefit, setEditingBenefit] = useState<RowData | null>(null)
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const benefitsQuery = useQuery({
    queryKey: ['benefits', page, searchTerm, filters, employeeId],
    queryFn: () =>
      fetchBenefits({
        search: searchTerm || undefined,
        page,
        page_size: itemsPerPage,
        type: filters.type?.join(','),
        frequency: filters.frequency?.join(','),
        employee: employeeId ? String(employeeId) : filters.employee?.join(','),
      }),
  })

  const benefitsList = benefitsQuery.data?.data || []

  // Remove team color mapping - no longer needed
  // --- Filter: Employee (infinite scroll) - only show if not filtered by employeeId ---
  const employeeFilterQuery = useInfiniteQuery({
    queryKey: ['filter-employees', 10, ''],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: '',
        page: pageParam,
        pageSize: 10,
        filters: {},
        minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: !employeeId,
  })
  const employeeFilterList = useMemo(
    () =>
      (employeeFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string | number; first_name?: string; last_name?: string; work_email?: string }[] }) =>
        (p.data ?? []).map((e) => ({
          id: String(e.id),
          name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.work_email || String(e.id),
        }))
      ),
    [employeeFilterQuery.data]
  )


  // --- Delete mutation ---
  const deleteMutation = useMutation<string[], Error, string[]>({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteBenefit(id)))
      return ids
    },
    onSuccess: () => {
      handleToast(toast, true, t('common.states.success'), t('messages.deleteSuccess'))
      queryClient.invalidateQueries({
        queryKey: ['benefits'],
      })
      setSelectedRows([])
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('common.states.error'), error?.message || t('messages.deleteFailed'))
    },
  })

  const handleDeleteBenefits = () => {
    if (selectedRows.length === 0) return
    deleteMutation.mutate(selectedRows)
  }
  const exportSelectedOrAllToXls = async () => {
  try {
    const exportIds =
      selectedRows.length > 0 &&
      selectedRows.length !== (benefitsList.length || 0)
        ? selectedRows
        : undefined

    setIsExporting(true)

    await exportBenefitsToXls({
      search: searchTerm,
      filters: employeeId
        ? { ...filters, employee: [String(employeeId)] }
        : filters,
      ids: exportIds,
    })

    handleToast(
      toast,
      true,
      t('workforce.messages.exportSuccess'),
      t('workforce.messages.exportSuccessMessage', { format: 'XLS' }),
    )
  } catch (e) {
    console.error(e)
    handleToast(
      toast,
      false,
      t('workforce.messages.exportFailed'),
      t('workforce.messages.exportFailedMessage'),
    )
  } finally {
    setIsExporting(false)
    setIsExportDropdownOpen(false)
  }
}

const exportSelectedOrAllToCsv = async () => {
  try {
    const exportIds =
      selectedRows.length > 0 &&
      selectedRows.length !== (benefitsList.length || 0)
        ? selectedRows
        : undefined

    setIsExporting(true)

    await exportBenefitsToCsv({
      search: searchTerm,
      filters: employeeId
        ? { ...filters, employee: [String(employeeId)] }
        : filters,
      ids: exportIds,
    })

    handleToast(
      toast,
      true,
      t('workforce.messages.exportSuccess'),
      t('workforce.messages.exportSuccessMessage', { format: 'CSV' }),
    )
  } catch (e) {
    console.error(e)
    handleToast(
      toast,
      false,
      t('workforce.messages.exportFailed'),
      t('workforce.messages.exportFailedMessage'),
    )
  } finally {
    setIsExporting(false)
    setIsExportDropdownOpen(false)
  }
}

  // --- Selection helpers ---
  const currentPageRowIds = benefitsList.map((row: RowData) => String(row.id))
  const allSelected = currentPageRowIds.length > 0 && currentPageRowIds.every((id: string) => selectedRows.includes(id))
  const isIndeterminate =
    currentPageRowIds.length > 0 && currentPageRowIds.some((id: string) => selectedRows.includes(id)) && !allSelected

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const idStr = String(id)
    if (isChecked) setSelectedRows((prev) => [...prev, idStr])
    else setSelectedRows((prev) => prev.filter((x) => x !== idStr))
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) setSelectedRows((prev) => [...prev, ...currentPageRowIds.filter((id: string) => !prev.includes(id))])
    else setSelectedRows((prev) => prev.filter((id: string) => !currentPageRowIds.includes(id)))
  }

  const maybeT = (key: string, fallback: string) => {
    const translated = t(key)
    return translated !== key ? translated : fallback
  }

  const columns: Column[] = useMemo(() => {
    return [
      ...(!isDisabled ? [{ label: '', accessor: 'select', type: 'select' as const, width: '5%' }] : []),
      {
        label: t('common.fields.assignedTo'),
        accessor: 'assigned_to',
        type: 'custom' as const,
        Cell: ({ row }: { row: RowData }) => {
          // Show employee if assigned to employee
          if (row.employee && !row.team && !row.country_of_work) {
            const employeeObj = typeof row.employee === 'object' ? row.employee : null
            const name = employeeObj
              ? `${employeeObj.first_name || ''} ${employeeObj.last_name || ''}`.trim() || t('common.labels.na')
              : t('common.labels.na')
            const profilePicture = employeeObj?.profile_picture
            return (
              <AvatarCell image={profilePicture} name={name} />
            )
          }
          // Show team if assigned to team
          if (row.team) {
            const teamObj = typeof row.team === 'object' ? row.team : null
            const teamName = teamObj?.name || t('common.labels.na')
            return (
              <Text isTruncated textStyle="l-xs">
                {teamName}
              </Text>
            )
          }
          // Show country if assigned to country
          if (row.country_of_work) {
            return (
              <Flex alignItems="center" gap="8px">
                <CircleFlag
                  countryCode={row.country_of_work.toLowerCase()}
                  style={{ width: '24px', height: '24px' }}
                />
                <Text isTruncated textStyle="l-xs">
                  {getCountryNameByCode(row.country_of_work) || row.country_of_work}
                </Text>
              </Flex>
            )
          }
          return <Text textStyle="l-xs">{t('common.labels.na')}</Text>
        },
      },
      {
        label: t('common.fields.type'),
        accessor: 'type',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const fallback = BENEFIT_TYPE_FALLBACK[row.type] || row.type
          const label = maybeT(`workforce.benefits.types.${row.type}`, fallback)
          const config = BENEFIT_TYPE_CONFIG[row.type] || BENEFIT_TYPE_CONFIG.other

          return (
            <Tooltip label={label} placement="top" hasArrow>
              <Flex
                align="center"
                gap="8px"
                px="12px"
                h="28px"
                minW={0}
                maxW="100%"
                borderRadius="14px"
                bg={config.bg}
                color={config.color}
                borderWidth="1px"
                borderColor={config.color}
                display="inline-flex"
                overflow="hidden"
              >
                <Icon as={config.icon} boxSize="16px" flexShrink={0} />
                <Text textStyle="l-xs" fontWeight="600" noOfLines={1} minW={0}>
                  {label}
                </Text>
              </Flex>
            </Tooltip>
          )
        },
      },
      {
        label: t('payroll.table.columns.frequency', 'Frequency'),
        accessor: 'frequency',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const fallback = BENEFIT_FREQUENCY_FALLBACK[row.frequency] || row.frequency
          const label = maybeT(`workforce.benefits.frequencies.${row.frequency}`, fallback)
          return <Text textStyle="l-xs">{label}</Text>
        },
      },
      {
        label: t('payroll.table.columns.value', 'Value'),
        accessor: 'value',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const symbol = currencySymbolMap(row.currency || 'EUR') ?? row.currency ?? '€'
          return (
            <Text textStyle="l-xs">
              {symbol}{Number(row.value || 0).toLocaleString()}
            </Text>
          )
        },
      },
      {
        label: t('payroll.table.columns.amountEur', 'Amount (EUR)'),
        accessor: 'amount_eur',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => (
          <Text textStyle="l-xs">€ {Number(row.amount_eur || 0).toLocaleString()}</Text>
        ),
      },
      {
        label: t('common.fields.date'),
        accessor: 'benefit_date',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => (
          <Text textStyle="l-xs">{row.benefit_date ? formatDate(row.benefit_date) : '-'}</Text>
        ),
      },
      {
        label: t('payroll.table.columns.addedBy', 'Added By'),
        accessor: 'added_by',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const addedByObj = typeof row.added_by === 'object' ? row.added_by : null
          const name = addedByObj
            ? `${addedByObj.first_name || ''} ${addedByObj.last_name || ''}`.trim() || '-'
            : '-'
          const profilePicture = addedByObj?.profile_picture
          return (
            <AvatarCell image={profilePicture} name={name} />
          )
        },
      },
    ]
  }, [t, isDisabled])

  return (
    <>
      <Box width="100%" height="100%">
        <Card width="100%" p="0">
          <Grid>
            <Box>
              <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
                <SearchInput onSearch={(val) => { setSearchTerm(val); setPage(1) }} />

                <Flex gap="8px">
  {!isDisabled && selectedRows.length > 0 ? (
    <Button
      onClick={handleDeleteBenefits}
      variant="error"
      isLoading={deleteMutation.isPending}
    >
      <Icon fill="none" color="error.100" mr={2}>
        <Trash04Icon />
      </Icon>
      {t('common.modals.actionCountTitle', {
        action: t('common.actions.delete'),
        count: selectedRows.length,
        item: t('benefit', { count: selectedRows.length }),
      })}
    </Button>
  ) : (
    <>
      {!employeeId && (
        <FilterButton
          label={t('common.fields.employee')}
          options={employeeFilterList}
          onApply={(selected) => updateFilter('employee', selected)}
        />
      )}

      <FilterButton
        label={t('common.fields.type')}
        options={BENEFIT_TYPE_OPTIONS}
        onApply={(selected) => updateFilter('type', selected)}
      />

      <FilterButton
        label={t('payroll.table.columns.frequency', 'Frequency')}
        options={BENEFIT_FREQUENCY_OPTIONS}
        onApply={(selected) => updateFilter('frequency', selected)}
      />

      <Box position="relative">
        <Button
          variant="white"
          onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
          disabled={!benefitsList.length || isExporting}
          size="sm"
          h="32px"
          px="14px"
          fontSize="13px"
          fontWeight="600"
          borderRadius="8px"
        >
          <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}>
            <ExportIcon />
          </Icon>
          {isExporting
            ? t('common.states.exporting')
            : selectedRows.length > 0
              ? t('workforce.people.exportSelected', { count: selectedRows.length })
              : t('workforce.people.exportAll')}
        </Button>

        {isExportDropdownOpen && (
          <Box
            position="absolute"
            right="0"
            width="196px"
            height="92px"
            borderRadius="8px"
            mt={2}
            boxShadow="md"
            backgroundColor="white"
            zIndex="1000"
          >
            <Flex direction="column" h="100%">
              <Button
                variant="dropdown"
                height="50%"
                borderRadius="8px 8px 0 0"
                onClick={exportSelectedOrAllToXls}
                fontSize="sm"
              >
                {t('workforce.people.exportAsXLS')}
              </Button>
              <Button
                variant="dropdown"
                height="50%"
                borderRadius="0 0 8px 8px"
                onClick={exportSelectedOrAllToCsv}
                fontSize="sm"
              >
                {t('workforce.people.exportAsCSV')}
              </Button>
            </Flex>
          </Box>
        )}
      </Box>

      {!isDisabled && (
        <Button
          variant="mono"
          onClick={() => {
            setEditingBenefit(null)
            setIsAddModalOpen(true)
          }}
        >
          <Icon w="14px" h="14px" viewBox="0 0 24 24" fill="none" color="white">
            <PlusIcon />
          </Icon>
          {`${t('common.actions.add')} ${t('workforce.people.tabs.benefits')}`}
        </Button>
      )}
    </>
  )}
</Flex>
              </Flex>

              {benefitsQuery.isLoading ? (
                <LoadingTable columns={columns} />
              ) : (
                <Box minHeight="58vh">
                  <MasterTable
                    columns={columns}
                    data={benefitsList}
                    onRowClick={(row) => {
                      if (isDisabled) {
                        // View only - could open a view modal in the future
                        return
                      }
                      setEditingBenefit(row)
                      setIsAddModalOpen(true)
                    }}
                    onOpenNewTab={(row) => {
                      if (row.employee && !employeeId) {
                        window.open(`/user/workforce/people/${row.employee}?tab=benefits`, '_blank')
                      }
                    }}
                    selectedRows={selectedRows}
                    onSelectAll={handleSelectAll}
                    onSelectRow={handleSelectRow}
                    allSelected={allSelected}
                    isIndeterminate={isIndeterminate}
                    tableType="people"
                  />
                </Box>
              )}

              <Pagination
                currentPage={page}
                itemsPerPage={itemsPerPage}
                maxPage={benefitsQuery.data?.max_page || 0}
                totalItems={benefitsQuery.data?.count || 0}
                handlePreviousPage={() => setPage(page - 1)}
                handleNextPage={() => setPage(page + 1)}
                handlePageClick={(p: number) => setPage(p)}
              />
            </Box>
          </Grid>
        </Card>
      </Box>

      <AddBenefitModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          setEditingBenefit(null)
        }}
        employeeId={employeeId}
        benefit={editingBenefit}
        readOnly={isDisabled}
      />
    </>
  )
}

export default BenefitsTable
